import {
  Avatar,
  Box,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from "@mui/material";
import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import bg from "../../@shared/assets/mydecksbg.png";
import NeoButton from "../NeoButton";
import { useAuthStore } from "../../@shared/store/useAuthStore";
import { MainContext } from "../../App";
import {
  Add,
  ArrowForwardIos,
  Check,
  DoNotDisturb,
  LegendToggle,
  LoopRounded,
  MenuRounded,
  RecyclingRounded,
  ReplayCircleFilled,
} from "@mui/icons-material";
import { useTutorial } from "../../@shared/utils/useTutorial";
import TutorialInfinityOrb from "../TutorialInfinityOrb";
import { motion } from "framer-motion";
import InfinityCard from "../InfinityCard";
import {
  tutorialPlayers,
  testHands,
} from "../../@shared/utils/tutorialSequenceSteps";
import CurrentPlayer from "../gamePlay/CurrentPlayer";
import SpinWheel from "../gamePlay/SpinWheel";
import FightAnimation from "../gamePlay/FightAnimation";
import SlapCardAnimation from "../gamePlay/SlapCardAnimation";
import TradeCardAnimation from "../gamePlay/TradeCardAnimation";
import PlayerResultCard from "../gamePlay/PlayerResultCard";
import PlayerPlatform from "../gamePlay/PlayerPlatform";
import { parseCardEffect } from "../../@shared/game/deckEngine";
import { getElementalBonus } from "../../@shared/utils/Utils";
import Toast from "../Toast";
import { db } from "../../@shared/firebase/config";
import { doc, updateDoc } from "firebase/firestore";

const PracticeGameBoard = () => {
  const { Theme, setInfo, setMessage } = React.useContext(MainContext);
  const { userData } = useAuthStore();
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [players, setPlayers] = useState(null);
  const [hands, setHands] = useState(null);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [currentPlayerTurn, setCurrentPlayerTurn] = useState(1);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerMenu, setShowPlayerMenu] = useState(false);
  const [cardLayout, setCardLayout] = useState(null);

  // PLAYER MOVE(s) STATE =============================================
  const [playerMove, setPlayerMove] = useState(true);
  const [playerMoveType, setPlayerMoveType] = useState(null);
  const [numberSpun, setNumberSpun] = useState(null);
  const [cpMovesLeft, setCPMovesLeft] = useState(2);
  const [moves, setMoves] = useState([]);

  //  ANIMATION STATE =========================================
  const [activeAnimation, setActiveAnimation] = useState(null); // 🎯 PlayerPlatform exclusive
  const [attackAnimation, setAttackAnimation] = useState(null); // 🎴 SlapCardAnimation exclusive
  const [activeCardPlayAnimation, setActiveCardPlayAnimation] = useState(null); // 🎴 SlapCardAnimation exclusive
  const [showTradeAnimation, setShowTradeAnimation] = useState(false);

  const [showSlapAnimation, setShowSlapAnimation] = useState(false);
  const [slapCardData, setSlapCardData] = useState(null);
  const [finishedDrawAnimation, setFinishedDrawAnimation] = useState(false);

  const { progress, sequence, advanceStep } = useTutorial(userData?.id);

  const lastAnimationTimestampRef = useRef(false);
  const hasProcessedTurnRef = useRef("");

  const selectCard = (card, layout) => {
    // console.log(card);
    setSelectedCard(card);
    setCardLayout(layout);
  };

  const closeSelectedCard = () => {
    setTimeout(() => {
      setSelectedCard(null);
      setCardLayout(null);
    }, 300);
  };

  const syncMatchData = async (actingPlayerId, updatedPlayers, move = null) => {
    try {
      console.log("🔄 Syncing match data for player:", actingPlayerId);
      setPlayers(updatedPlayers);
      advanceStep();
      if (move) {
        setMoves((prev) => [move, ...prev]);
      }
      if (cpMovesLeft === 1) {
        if (currentPlayerTurn === 1) {
          setCurrentPlayerTurn(3);
        } else {
          setCurrentPlayerTurn(1);
        }
        setCPMovesLeft(2);
      } else {
        setCPMovesLeft(1);
      }
    } catch (err) {
      console.error("❌ Failed to sync match data:", err);
    }
  };

  const handleAttack = async () => {
    if (!currentPlayer || !selectedPlayer || numberSpun == null) return;

    const playersCopy = [...players];
    const opponentIndex = playersCopy.findIndex(
      (p) => p.id === selectedPlayer.id
    );
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === currentPlayer.id
    );

    const opponent = { ...playersCopy[opponentIndex] };
    let attacker = { ...playersCopy[currentPlayerIndex] };

    const attackerCharacter = attacker.character;
    const defenderCharacter = opponent.character;
    const attackerElement = attackerCharacter?.element;
    const defenderElement = defenderCharacter?.element;

    let criticalHit = false;
    let totalDamage = 0;
    let elementalBonus = null;

    const totalAccuracy =
      Number(attackerCharacter?.accuracy) +
      Number(
        attackerCharacter?.buffs?.accuracy?.reduce(
          (acc, buff) => acc + buff.amt,
          0
        ) || 0
      );

    if (totalAccuracy >= numberSpun) {
      totalDamage =
        Number(attackerCharacter?.attack) +
        Number(
          attackerCharacter?.buffs?.attack?.reduce(
            (acc, buff) => acc + buff.amt,
            0
          ) || 0
        );

      if (attackerCharacter.accuracy === numberSpun) {
        totalDamage += 1;
        criticalHit = true;
      }

      //   add elemental damage here
      if (attackerElement && defenderElement) {
        console.warn("Elemental Advantage!");
        elementalBonus = getElementalBonus(attackerElement, defenderElement);
        console.warn("+1 damage bonus");
        if (elementalBonus === 1) {
        } else if (elementalBonus === -1) {
          console.warn("-1 damage penalty");
        }
        totalDamage += elementalBonus;
      }

      if (defenderCharacter.stance === "defense") {
        // Damage is applied to defense first
        if (defenderCharacter.defense >= totalDamage) {
          defenderCharacter.defense -= totalDamage;
        } else {
          const remainingDamage = totalDamage - defenderCharacter.defense;
          defenderCharacter.defense = 0;
          defenderCharacter.stance = "attack";
          defenderCharacter.health -= remainingDamage;
        }
      } else {
        // Apply damage directly to health
        defenderCharacter.health -= totalDamage;
      }

      // Check if the defender is dead
      if (defenderCharacter.health <= 0) {
        defenderCharacter.health = 0;
        defenderCharacter.isDead = true;
      }

      console.log(
        criticalHit
          ? `It's a Critical Hit! ${defenderCharacter.name} now has ${defenderCharacter.health} health`
          : `Hit! ${defenderCharacter.name} now has ${defenderCharacter.health} health`
      );
    } else {
      console.log("Missed!");
    }

    // Clear instant accuracy/attack buffs after attack is made
    ["attack", "accuracy"].forEach((stat) => {
      if (Array.isArray(attacker.character.buffs[stat])) {
        attacker.character.buffs[stat] = attacker.character.buffs[stat].filter(
          (buff) => !(buff.duration === 0)
        );
      }
    });

    setSelectedPlayer(null);
    setPlayerMoveType(null);
    setNumberSpun(null);

    if (opponent.character.isDead) {
      console.log(`${opponent.name} is dead!`);

      //   const matchSnap = await getDoc(matchRef);
      //   const matchDataFresh = matchSnap.data();
      //   const currentEliminated = matchDataFresh?.eliminated || [];

      //   const alreadyEliminated = currentEliminated.find(
      //     (e) => e.id === opponent.id
      //   );

      //   if (!alreadyEliminated) {
      //     const eliminatedEntry = {
      //       name: opponent.name,
      //       id: opponent.id,
      //       character: opponent.character.name,
      //       eliminatedBy: {
      //         name: attacker.name,
      //         id: attacker.id,
      //         character: attacker.character.name,
      //       },
      //     };

      //     // const updatedEliminated = [eliminatedEntry, ...currentEliminated];

      //     // await updateDoc(matchRef, {
      //     //   eliminated: updatedEliminated,
      //     // });

      //     console.log(`Updated matchRef with eliminated player ${opponent.name}`);
      //   }
    }

    const attackMove = {
      type: totalDamage === 0 ? "attack - missed" : "attack",
      attacker: attacker.name,
      target: opponent.name,
      damage: totalDamage,
      criticalHit,
      timestamp: Date.now(),
    };

    syncMatchData(attacker.id, playersCopy, attackMove);
    console.log("Setting from here 1");
  };

  const handlePlayCard = async () => {
    if (!selectedCard || !selectedPlayer) return;

    let playersCopy = [...players];
    const selectedPlayerIndex = playersCopy.findIndex(
      (p) => p.id === selectedPlayer.id
    );
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === currentPlayer.id
    );

    console.log("indecies", currentPlayerIndex, selectedPlayerIndex);

    if (selectedPlayerIndex === -1 || currentPlayerIndex === -1) return;

    if (selectedPlayerIndex !== currentPlayerIndex) {
      console.log("This is not happening");
      setMessage("Please Follow The Tutorial.");

      setInfo(true);
      return;
    }

    let targetPlayer = { ...playersCopy[selectedPlayerIndex] };
    let actingPlayer = { ...playersCopy[currentPlayerIndex] };

    // === Parse effect logic
    const { affectedAreas, amount, duration, isAid } =
      parseCardEffect(selectedCard);

    // Target logic: self or other
    const recipient =
      selectedPlayer.id === actingPlayer.id ? actingPlayer : targetPlayer;

    affectedAreas.forEach((key) => {
      let area = key.toLowerCase(); // normalize
      if (area === "turn loss") {
        area = "turnLoss";
      }

      const isInstant = duration === 0;
      if (isInstant && area !== "attack" && area !== "accuracy") {
        // Health, Defense, & turnLoss
        const initValue = recipient.character[area]
          ? Number(recipient.character[area])
          : 0;
        recipient.character[area] = initValue + amount;
      } else {
        if (!recipient.character.buffs[area]) {
          recipient.character.buffs[area] = [];
        }

        const buffObj = {
          amt: isAid ? amount : -amount,
          duration,
          // instant: isInstant,
        };
        recipient.character.buffs[area].push(buffObj);
      }
    });

    let hand = hands?.[actingPlayer.id];

    // Remove the selected card
    // FIXED card removal logic
    const cardIndex = hand.findIndex(
      (card) =>
        card.id === selectedCard.id &&
        card.name === selectedCard.name &&
        card.type === selectedCard.type
    );

    if (cardIndex !== -1) {
      hand.splice(cardIndex, 1); // removes ONLY ONE instance
    }

    // Update players
    playersCopy[selectedPlayerIndex] = targetPlayer;
    playersCopy[currentPlayerIndex] = actingPlayer;

    const move = {
      type: "playCard",
      player: actingPlayer.name,
      card: { ...selectedCard },
      target: targetPlayer.name,
      effect: { affectedAreas, amount, duration, isAid },
      timestamp: Date.now(),
    };

    syncMatchData(actingPlayer.id, playersCopy, move);

    setSelectedCard(null);
    setCardLayout(null);
    setShowPlayerMenu(false);
    setSelectedPlayer(null);
    setPlayerMoveType(null);
    setNumberSpun(null);
  };

  const handleCardAction = (action) => {
    console.log("this is the action", action);
    if (action === "playCard") {
      setCardLayout(null);
      handlePlayCard();
    }
    setShowPlayerMenu(false);
  };

  const updatePlayerStance = () => {
    const playersCopy = [...players];
    const currentPlayerIndex = playersCopy.findIndex(
      (p) => p.id === userData.id
    );
    const currentPlayer = playersCopy[currentPlayerIndex];
    let currentCharacter = { ...currentPlayer }.character;
    const currentStance = currentCharacter.stance;
    //
    const newStance = currentStance === "defense" ? "attack" : "defense";
    currentCharacter.stance = newStance;
    playersCopy[currentPlayerIndex] = currentPlayer;
    // setPlayers(playersCopy);
    setPlayerMoveType(null);
    const stanceMove = {
      type: "stanceChange",
      player: currentPlayer.name,
      newStance,
      timestamp: Date.now(),
    };

    if (progress?.currentStepIndex === 3) {
      setPlayers(playersCopy);
      advanceStep();
    }

    syncMatchData(currentPlayer.id, playersCopy, stanceMove);
  };

  const getTextBoxPosition = () => {
    switch (progress?.currentStepIndex) {
      case 0:
        return {
          top: "10%",
        };
      case 1:
        return {
          top: "35%",
        };
      case 2:
        return {
          top: "20%",
        };
      case 3:
        return {
          top: "15%",
        };
      case 4:
        return {
          top: "15%",
        };
      case 5:
        return {
          top: "15%",
        };
      default:
        return {
          top: "15%",
        };
    }
  };

  const handleNextStep = async () => {
    // 1, 2, 5, 6, 9

    if (progress.currentStepIndex === sequence.steps.length - 1) {
      await db.collection("users").doc(userData.id).update({
        completedTutorial: true,
      });
      advanceStep();
      return;
    } else {
      if (progress.currentStepIndex === 1) {
        setSelectedPlayer(null);
      }
      if (progress.currentStepIndex === 6) {
        setSelectedCard(hands["bot_1"][0]);
        setTimeout(async () => {
          let updatedPlayers = [...players].map((player) =>
            player.id === "bot_1"
              ? {
                  ...player,
                  character: {
                    ...player.character,
                    attack: player.character.attack + 1,
                  },
                }
              : player
          );
          let move = {
            player: "BotPlayer 1",
            type: "playCard",
            card: hands["bot_1"][0],
            target: "BotPlayer 1",
            effect: {
              affectedAreas: ["attack"],
              amount: 1,
              duration: 0,
              isAid: false,
            },
            timestamp: Date.now(),
          };
          await syncMatchData("bot_1", updatedPlayers, move);
        }, 1000);
      }

      if (progress.currentStepIndex === 8) {
        let playersCopy = [...players];
        let attacker = playersCopy.find((player) => player.id === "bot_1");
        let opponent = playersCopy.find((player) => player.id === userData.id);

        let attackerCharacter = attacker.character;
        let defenderCharacter = opponent.character;

        let criticalHit = false;
        let totalDamage = 7;

        if (defenderCharacter.defense >= totalDamage) {
          defenderCharacter.defense -= totalDamage;
        } else {
          const remainingDamage = totalDamage - defenderCharacter.defense;
          defenderCharacter.defense = 0;
          defenderCharacter.stance = "attack";
          defenderCharacter.health -= remainingDamage;
        }

        ["attack", "accuracy"].forEach((stat) => {
          if (Array.isArray(attackerCharacter.buffs[stat])) {
            attackerCharacter.buffs[stat] = attackerCharacter.buffs[
              stat
            ].filter((buff) => !(buff.duration === 0));
          }
        });

        const attackMove = {
          type: "attack",
          attacker: attacker.name,
          target: opponent.name,
          damage: totalDamage,
          criticalHit,
          timestamp: Date.now(),
        };
        syncMatchData(attacker.id, playersCopy, attackMove);
      }
      advanceStep();
    }
  };

  useEffect(() => {
    if (!players && !hands) {
      let newPlayers = [...tutorialPlayers].map((player, i) => {
        if (player.id === "test_player_id") {
          return { ...player, id: userData.id, name: userData.username };
        }
        return player;
      });
      setPlayers(newPlayers);

      let copyOfTestHands = { ...testHands };
      const value = copyOfTestHands["test_player_id"];
      // Step 2: Delete the old key
      delete copyOfTestHands["test_player_id"];
      copyOfTestHands[userData.id] = value;
      console.log("Copy of hands", copyOfTestHands);
      setHands(copyOfTestHands);

      setCurrentPlayerTurn(1);
      setCurrentPlayer(newPlayers[1]);
      setIsMyTurn(true);
    }
  }, [players, hands]);

  //   useEffect(() => {
  //     if (progress && progress.currentStepIndex === 1 && !selectedPlayer) {
  //       setSelectedPlayer(tutorialPlayers[3]);
  //     }
  //     if (progress && progress.currentStepIndex >= 4 && !moves) {
  //       setMoves((prev) => [
  //         ...prev,
  //         {
  //           type: "stanceChange",
  //           player: currentPlayer.name,
  //           newStance: "defense",
  //           timestamp: Date.now(),
  //         },
  //       ]);
  //     }

  //     if (progress && progress.currentStepIndex === 5 && hands) {
  //       setCPMovesLeft(1);
  //       setPlayerMoveType("playCard");
  //       setSelectedCard(hands[userData.id][4]);
  //     }
  //   }, [progress, hands]);

  useEffect(() => {
    if (
      userData &&
      userData?.tutorialProgress?.currentSequence === "guidedBattle" &&
      userData?.tutorialProgress?.currentStepIndex !== 0
    ) {
      //reset tutorial progress
      const userRef = doc(db, "users", userData.id);
      updateDoc(userRef, {
        "tutorialProgress.currentSequence": "guidedBattle",
        "tutorialProgress.currentStepIndex": 0,
      });
    }
  }, []);

  useEffect(() => {
    if (!moves?.length) return;

    let movesCopy = [...moves].sort((a, b) => a.timestamp - b.timestamp);

    const lastMove = movesCopy[movesCopy.length - 1];
    if (!lastMove) return;
    const { type, attacker, player, target, timestamp, card } = lastMove;

    // if (timestamp === lastAnimationTimestampRef.current) return;
    console.log("we get here", movesCopy);

    // 🔥 ATTACK → PlayerPlatform animation directly
    if (type === "attack") {
      setAttackAnimation(lastMove);

      lastAnimationTimestampRef.current = timestamp;
    }

    // 🎴 PLAY CARD → Triggers Slap animation
    else if (type === "playCard" && card) {
      console.log("🎴 Triggering SlapCardAnimation...");
      setActiveCardPlayAnimation({
        type: "playCard",
        player,
        target,
        card,
        timestamp,
      });

      lastAnimationTimestampRef.current = timestamp;
    } else if (type === "tradeCard") {
      setShowTradeAnimation(true);
      lastAnimationTimestampRef.current = timestamp;
    }
  }, [moves]);

  useEffect(() => {
    if (!activeAnimation || activeAnimation.type !== "playCard") return;
    console.log("🎴 Triggering SlapCardAnimation...");

    const actor = players.find(
      (p) =>
        p.name === activeAnimation.player ||
        p.username === activeAnimation.player
    );
    const lastMove = moves?.[moves.length - 1];

    if (actor && lastMove?.card) {
      setSlapCardData(lastMove.card); // boom — now you get full card details
      setShowSlapAnimation(true);
    }
  }, [activeAnimation]);

  // ! RESPONSIBLE FOR HANDLING ATTACK ===============================================
  useEffect(() => {
    if (numberSpun) {
      if (playerMoveType === "attack") {
        handleAttack();
      }
    }
  }, [numberSpun]);

  if (!players && !hands) return <></>;

  return (
    <Grid
      container
      xs={12}
      sx={{
        p: { xs: 0, md: 2 },
        height: "100%",
        width: "100%",
        backgroundImage: `url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          p: { xs: 0 },
        }}
      >
        {selectedCard && cardLayout && (
          <Box
            // onClick={closeSelectedCard}
            style={{
              position: "absolute",
              position: "absolute",
              width: "100%",
              height: "100%",
              zIndex: 2500,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
            }}
          >
            <motion.div
              style={{
                position: "absolute",
                top: "40%",
                left: window.innerWidth >= 900 ? "36%" : "20%",
                width:
                  window.innerWidth >= 900
                    ? cardLayout.width * 3
                    : cardLayout.width,
                height:
                  window.innerWidth >= 900
                    ? cardLayout.height * 3
                    : cardLayout.width,
                zIndex: 1000,
              }}
              initial={{ opacity: 0, y: 300 }}
              animate={{ opacity: 1, y: -0 }}
              transition={{
                type: "spring",
                damping: 20,
                duration: 0.2,
                stiffness: 200,
                delay: 0.2,
              }}
            >
              <InfinityCard
                {...selectedCard}
                heightPer={100}
                // heightPer={window.innerWidth >= 900 ? 70 : 100}
                containerHeight={
                  window.innerWidth > 900
                    ? window.innerHeight * 0.38
                    : window.innerHeight * 0.38
                }
                borderPx={4}
              />
            </motion.div>
          </Box>
        )}

        <Grid
          item
          xs={12}
          md={7}
          sx={{
            position: "relative",
            backgroundColor: "#00000333",
            borderRadius: 2,
            height: "100%",
            padding: { xs: 0, md: 1 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: getTextBoxPosition(),
              left: "50%",
              transform: "translateX(-50%)",
              background: "white",
              borderRadius: 2,
              padding: 2.5,
              height: "fit-content",
              width: "80%",
              zIndex: 9998,
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: "100%",
                height: "inherit",
              }}
            >
              {progress?.currentStepIndex &&
                [1, 2, 6, 7, 8, 11, 12].find(
                  (i) => i === progress.currentStepIndex
                ) && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-50px",
                      right: { xs: "85%", md: "95%" },
                    }}
                  >
                    <NeoButton
                      isIcon
                      icon={<ArrowForwardIos />}
                      color={Theme.palette.tirciary.main}
                      onClick={handleNextStep}
                    />
                  </Box>
                )}
              <Typography
                variant="h6"
                sx={{
                  whiteSpace: "pre-line",
                  width: "100%",
                  textAlign: "center",
                  fontSize: { xs: "10px", md: "12px" },
                  position: "relative",
                  bottom: { xs: "8px", md: "0px" },
                }}
              >
                {sequence?.steps?.[progress.currentStepIndex]?.text}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: { xs: "15%", md: sequence?.orbPosition?.right },
              top: { xs: "25%", md: sequence?.orbPosition?.top },
              transform: {
                xs: `scale(${sequence?.orbPosition?.scale})`,
                md: "scale(2)",
              },
              zIndex: 9999,
            }}
          >
            <TutorialInfinityOrb currentSequence={"guidedBattle"} />
          </Box>
          <Grid
            item
            container
            xs={12}
            sx={{
              ...styles.container,
              position: "relative",
              display: { xs: "flex" },
              border: { xs: "", md: "1px solid #ccc" },
              borderRadius: 2,
              height: "100%",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "1%",
                right: "1%",
                zIndex: 9999,
              }}
            ></Box>

            {players && players.length > 0 && (
              <>
                <Toast
                  timeTilClose={2500} //in ms
                  onFinish={() => {
                    {
                    }
                  }}
                />

                {/* Game Board - Players map */}
                <Box sx={styles.playersContainer}>
                  {players.map((player, index) => (
                    <PlayerPlatform
                      key={index}
                      {...player}
                      playerTurn={players[currentPlayerTurn]?.id === player.id}
                      isSelected={selectedPlayer?.id === player.id}
                      onSelect={() => {
                        if (selectedPlayer?.id !== player.id) {
                          setSelectedPlayer(player);
                        } else {
                          setSelectedPlayer(null);
                        }

                        if (selectedCard) {
                          setShowPlayerMenu(true);
                        }
                      }}
                      // activeAnimation={() => console.log(activeAnimation)}
                      activeAnimation={activeAnimation}
                      cpMovesLeft={cpMovesLeft}
                      // nextTurn={nextTurn}
                      playerMoveType={playerMoveType}
                      lastMove={moves?.slice(-1)[0] || null}
                      isTutorial={true}
                    />
                  ))}
                </Box>

                {/* Game Board - Player Section */}
                {selectedPlayer && (
                  <motion.div
                    key={selectedPlayer.id}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      position: "absolute",
                      top: "10%",
                      left: 0,
                      zIndex: 100,
                      height: "30%",
                      width: "100%",
                    }}
                  >
                    <CurrentPlayer
                      {...selectedPlayer}
                      selectCard={selectCard}
                      isCurrentPlayer={false}
                    />
                  </motion.div>
                )}

                {userData && currentPlayer && (
                  <CurrentPlayer
                    {...currentPlayer}
                    hand={hands[userData.id]}
                    selectCard={selectCard}
                    isCurrentPlayer={true}
                    playerTurn={players[currentPlayerTurn]?.id === userData.id}
                    updatePlayerStance={updatePlayerStance}
                    setPlayerMoveType={setPlayerMoveType}
                    setSelectedPlayer={setSelectedPlayer}
                    handDisabled={
                      selectedPlayer !== null ||
                      selectedCard !== null ||
                      cpMovesLeft === 0
                    }
                    attackAnimation={attackAnimation}
                    disabledByToast={false}
                    finishedDrawAnimation={true}
                    isTutorial={true}
                    progress={progress}
                    advanceStep={advanceStep}
                  />
                )}

                {isMyTurn && selectedCard && cardLayout && playerMove && (
                  //  Box containing Button options for the selected card
                  <Box sx={styles.cardOptionsContainer}>
                    {/* TRADE CARD BUTTON */}
                    <Box
                      style={{
                        ...styles.moveButton,
                        flexDirection: "column",
                        width: 42,
                        marginBottom: 0,
                      }}
                    >
                      <NeoButton
                        icon={
                          <img
                            src={require("../../@shared/assets/icons/trade_card_icon.png")}
                            style={{ width: 30, height: 30 }}
                            alt=""
                          />
                        }
                        isIcon={true}
                        onClick={() => {
                          //
                        }}
                        width={100}
                        color={Theme.palette.primary.main}
                      />
                      <Typography style={styles.cardOptionText}>
                        Trade It
                      </Typography>
                    </Box>

                    {/* PLAY CARD BUTTON */}
                    <Box
                      style={{
                        ...styles.moveButton,
                        flexDirection: "column",
                        width: 42,
                        marginBottom: 0,
                      }}
                    >
                      <NeoButton
                        icon={
                          <img
                            src={require("../../@shared/assets/icons/play_card_icon.png")}
                            style={{ width: 30, height: 30 }}
                            alt=""
                          />
                        }
                        isIcon={true}
                        onClick={() => {
                          if (
                            progress.currentStepIndex === 5 ||
                            progress.currentStepIndex === 9
                          ) {
                            setPlayerMoveType("playCard");
                            setCardLayout(null);
                            setShowPlayerMenu(true);
                          } else {
                          }
                        }}
                        width={100}
                        color={Theme.palette.primary.main}
                      />
                      <Typography style={styles.cardOptionText}>
                        Play It
                      </Typography>
                    </Box>
                    {/* DISCARD CARD BUTTON */}

                    <Box
                      style={{
                        ...styles.moveButton,
                        flexDirection: "column",
                        width: 42,
                        marginBottom: 0,
                      }}
                    >
                      <NeoButton
                        icon={
                          <img
                            src={require("../../@shared/assets/icons/discard_icon.png")}
                            style={{ width: 30, height: 30 }}
                            alt=""
                          />
                        }
                        isIcon={true}
                        onClick={() => {
                          //
                        }}
                        width={100}
                        color={Theme.palette.secondary.main}
                      />
                      <Typography style={styles.cardOptionText}>
                        Discard It
                      </Typography>
                    </Box>
                  </Box>
                )}

                {isMyTurn && playerMoveType === "attack" && selectedPlayer && (
                  <motion.div
                    initial={{ opacity: 0, y: -40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{
                      position: "absolute",
                      bottom: "-15%",
                      left: 0,
                      zIndex: 1000,
                      width: "100%",
                    }}
                  >
                    <SpinWheel
                      numberSpun={numberSpun}
                      setNumberSpun={setNumberSpun}
                    />
                  </motion.div>
                )}

                {attackAnimation && (
                  <FightAnimation
                    attackAnimation={attackAnimation}
                    character={
                      players?.find(
                        (player) => player.name === moves[0].attacker
                      )?.character
                    }
                    onFinish={() => setAttackAnimation(null)}
                  />
                )}

                {activeCardPlayAnimation && (
                  <SlapCardAnimation
                    card={activeCardPlayAnimation.card}
                    onFinish={() => {
                      const targetPlayer = players.find(
                        (p) => p.name === activeCardPlayAnimation.target
                      );

                      if (targetPlayer) {
                        setActiveAnimation({
                          type: "playCard",
                          target: targetPlayer.name,
                          timestamp: activeCardPlayAnimation.timestamp,
                          fps: 18,
                        });
                      }

                      // 💥 Clean it up
                      setActiveCardPlayAnimation(null);
                    }}
                  />
                )}

                {showTradeAnimation && (
                  <TradeCardAnimation
                    onFinish={() => {
                      setShowTradeAnimation(false);
                    }}
                  />
                )}

                {/* ACTION BUTTON ===================== */}
                {players[currentPlayerTurn]?.id === userData.id &&
                  !players.find((p) => p.id === userData.id)?.character
                    ?.isDead && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 15,
                        right: { xs: "80%", md: 15 },
                        zIndex: 9998,
                      }}
                    >
                      <NeoButton
                        isIcon={true}
                        disabled={
                          !players[currentPlayerTurn]?.id === userData.id
                        }
                        icon={
                          <img
                            src={require("../../@shared/assets/icons/fist_icon.png")}
                            style={{ width: 30, height: 30 }}
                          />
                        }
                        color={Theme.palette.secondary.main}
                        onClick={() => {
                          if (
                            progress.currentStepIndex === 3 ||
                            progress.currentStepIndex > 7
                          ) {
                            setShowPlayerMenu(true);
                            setSelectedPlayer(null);
                          } else {
                            setMessage(
                              "Please follow the tutorial to continue"
                            );
                            setInfo(true);
                          }
                        }}
                      />
                    </Box>
                  )}

                {/* PLAYER ACTION MENU ===================== */}
                {showPlayerMenu && (
                  <Box sx={styles.moveSelectionContainer}>
                    {playerMove && (
                      <>
                        {!selectedCard && (
                          <Box sx={styles.moveButton}>
                            <NeoButton
                              text={"Stance"}
                              onClick={() => {
                                if (progress.currentStepIndex === 10) {
                                  setMessage(
                                    "Please follow the tutorial to continue"
                                  );
                                  setInfo(true);
                                } else {
                                  updatePlayerStance();
                                  setShowPlayerMenu(false);
                                }
                              }}
                              width={100}
                              color={Theme.palette.primary.main}
                              startAdorn={<LoopRounded />}
                              endAdorn={
                                <img
                                  alt=""
                                  src={
                                    currentPlayer?.character?.stance ===
                                    "defense"
                                      ? require("../../@shared/assets/icons/swords_icon2.png")
                                      : require("../../@shared/assets/icons/sheild.png")
                                  }
                                  style={styles.moveIcon}
                                />
                              }
                            />
                          </Box>
                        )}

                        {isMyTurn &&
                          currentPlayer?.character?.stance === "attack" &&
                          !selectedCard && (
                            <Box sx={styles.moveButton}>
                              <NeoButton
                                text="Attack"
                                onClick={() => {
                                  if (progress.currentStepIndex === 10) {
                                    setPlayerMoveType("attack");
                                    setShowPlayerMenu(false);
                                  } else {
                                    setMessage(
                                      "Please follow the tutorial to continue"
                                    );
                                    setInfo(true);
                                  }
                                }}
                                width={100}
                                color={Theme.palette.primary.main}
                                startAdorn={
                                  <img
                                    src={require("../../@shared/assets/icons/swords_icon2.png")}
                                    style={styles.moveIcon}
                                  />
                                }
                              />
                            </Box>
                          )}

                        {((selectedPlayer && playerMoveType === "playCard") ||
                          (selectedPlayer && playerMoveType === "tradeCard") ||
                          (selectedPlayer && playerMoveType === "attack")) && (
                          <>
                            <Box sx={styles.moveButton}>
                              <NeoButton
                                icon={<Check size={30} color="$text" />}
                                text="Confirm"
                                onClick={() => {
                                  if (
                                    progress?.currentStepIndex === 5 ||
                                    progress.currentStepIndex === 9
                                  ) {
                                    console.log("handling play card");
                                    handleCardAction(playerMoveType);
                                  }
                                }}
                                width={100}
                                color={Theme.palette.primary.main}
                              />
                            </Box>
                          </>
                        )}
                      </>
                    )}

                    <Box sx={styles.moveButton}>
                      <NeoButton
                        icon={<DoNotDisturb size={30} color="#000" />}
                        text="Cancel"
                        onClick={() => {
                          setSelectedPlayer(null);
                          setSelectedCard(null);
                          setShowPlayerMenu(false);
                          setSelectedCard(null);
                          setPlayerMoveType(null);
                        }}
                        width={100}
                        color={Theme.palette.secondary.main}
                      />
                    </Box>
                  </Box>
                )}

                {/* CHAT BUTTON ===================== */}
              </>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          xs={4}
          lg={2}
          sx={{
            display: { xs: "none", md: "none", lg: "block" },
            backgroundColor: "#00000333",
            height: "100%",
            padding: 1,
          }}
        >
          <Grid
            item
            container
            xs={12}
            sx={{
              ...styles.container,
              position: "relative",
              display: { xs: "flex" },
              border: "1px solid #ccc",
              borderRadius: 2,
              maxHeight: "100%",
              overflowY: "scroll",
              scrollbarWidth: "none",
            }}
          >
            <Typography
              variant="caption"
              component="h1"
              //transparent background
              sx={styles.text}
            >
              Move Tracker
            </Typography>

            <Grid
              item
              container
              xs={12}
              sx={{
                padding: 1,
                overflowY: "scroll",
                scrollbarWidth: "none",
                maxHeight: "calc(100% - 35px)",
              }}
            >
              {moves
                ?.sort((a, b) => b.timestamp - a.timestamp)
                .map((move, idx) => {
                  const movePlayer = move.attacker ?? move.player;
                  const targetPlayer = move.target
                    ? move.target
                    : move.defender;

                  const formattedMessage = () => {
                    if (move.type === "attack") {
                      return `attacked ${targetPlayer} for ${move.damage} damage.`;
                    }
                    if (move.type === "attack - missed") {
                      return `tried to attack ${targetPlayer} but missed.`;
                    }
                    if (move.type === "playCard") {
                      return `applied a ${move.card.name} on ${
                        targetPlayer === movePlayer ? "themselves" : move.target
                      }.`;
                    }
                    if (move.type === "turnLoss") {
                      return `was affected by a turn loss.`;
                    }
                    if (move.type === "tradeCard") {
                      return `traded a card with ${targetPlayer}.`;
                    }
                    if (move.type === "discardCard") {
                      return `discarded a card from thier hand.`;
                    }
                    if (move.type === "stanceChange") {
                      return `changed their stance to ${move.newStance}.`;
                    }
                    if (move.type === "playerSkipped") {
                      return `was skipped by the timer.`;
                    }
                  };

                  return (
                    <Grid
                      item
                      container
                      xs={12}
                      sx={{
                        width: "100%",
                        justifyContent: "space-between",
                        alignItems: "center",
                        border: "1px solid #fff",
                        background: `${Theme.palette.primary.dark}80`,
                        borderRadius: 1,
                        mb: 1,
                        padding: 0.5,
                      }}
                      key={idx}
                    >
                      <Box
                        style={{
                          width: 50,
                          height: 50,
                        }}
                      >
                        <img
                          src={
                            players?.find(
                              (p) =>
                                p.name === move.player ||
                                p.name === move.attacker
                            )?.character?.imageUrl
                          }
                          style={{
                            height: "100%",
                            width: "100%",
                            objectFit: "contain",
                          }}
                          alt={""}
                        />
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#fff",
                          width: "calc(100% - 60px)",
                          fontSize: 12,
                        }}
                      >
                        {formattedMessage()}
                      </Typography>
                      <Grid
                        item
                        container
                        xs={12}
                        style={{
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="h1"
                          sx={{
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: 12,
                          }}
                        >
                          {movePlayer}
                        </Typography>

                        <Typography
                          variant="caption"
                          component="h1"
                          sx={{
                            color: "#fff",
                            fontSize: 12,
                          }}
                        >
                          {new Date(move.timestamp).toLocaleTimeString()}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })}
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Grid>
  );
};

const styles = {
  container: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 9999,
    margin: "10px",
    width: 50,
  },
  text: { color: "#fff", fontWeight: "bold" },
  playersContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    transform: "skewX(-50deg)",
    width: { xs: "50%", md: "50%" },
    marginRight: { xs: "15%", md: "7%" },
    marginBottom: { xs: "0%", md: "10%" },
  },
  cardOptionsContainer: {
    zIndex: 2500,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center",
    width: "100%",
    height: "20%",
    backgroundColor: "#00000090",
    padding: "10px",
    borderRadius: "10px",
    position: "absolute",
    bottom: "0%",
    left: "50%",
    transform: "translateX(-50%)",
  },
  cardOptionText: {
    fontSize: "12px",
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
  },
  moveSelectionContainer: {
    zIndex: 9999,
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "fit-content",
    height: "auto",
    backgroundColor: "#00000050",
    padding: "5px",
    borderRadius: "10px",
    position: "absolute",
    bottom: "25px",
    right: "10px",
  },
  moveButton: {
    width: "200px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  moveIcon: {
    width: "24px",
    height: "24px",
    marginLeft: "10px",
  },
  moveText: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "10px",
  },
  unread: {
    position: "absolute",
    top: 3,
    right: 0,
    backgroundColor: "#FF0000",
    padding: "5px",
    borderRadius: "10px",
    borderWidth: "4px",
    borderColor: "#000",
    color: "#FFFFFF",
    width: "12px",
    height: "12px",
    zIndex: 1000,
  },
  promptContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#00000050",
    color: "#fff",
    padding: 20,
    borderRadius: 10,
    position: "absolute",
    top: "15%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
  promptText: {
    fontSize: 18,
    fontWeight: 900,
    marginBottom: 10,
    color: "#fff",
    textAlign: "center",
  },
};

export default PracticeGameBoard;
